import { useState } from 'react';
import type { Operaciones } from '../../../api/operaciones';
import type { CambiosCampo, CampoCatalogo, EtiquetaCatalogo, TipoCampo } from '../../../api/ops/clientesCatalogo';
import { ErrorApi } from '../../../api/problema';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Ayuda } from '../../../componentes/Ayuda';
import { Boton } from '../../../componentes/Boton';
import { Campo } from '../../../componentes/Campo';
import { Desplegable } from '../../../componentes/Desplegable';
import { Icono } from '../../../componentes/Icono';
import { Interruptor } from '../../../componentes/Interruptor';
import { OpcionRadio } from '../../../componentes/OpcionRadio';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { textoDeError } from '../../equipo/textos';
import { AYUDA, COLORES, TEXTOS, TIPO, TIPOS, claveDe, esClaro } from './textos';

export type Abierto = { tipo: 'etiqueta'; etiqueta: EtiquetaCatalogo | null } | { tipo: 'campo'; campo: CampoCatalogo | null };

/**
 * El `ETag` para el `If-Match`: el de la última respuesta de esa etiqueta o ese campo en la sesión o, si no lo hay, el
 * que da su lectura (`getTag`, `getCustomField`, G353). Si alguien la cambió entre medias, la API responde 412.
 */
async function versionDe(versiones: ReadonlyMap<string, string>, id: string, leer: () => Promise<{ version: string | null }>): Promise<string> {
    return versiones.get(id) ?? (await leer()).version ?? '';
}

/** Lo que la API dice que no se puede convertir (409 de `updateCustomField`): `values` si lo trae, si no, sus `errors`. */
function noConvertibles(e: ErrorApi): string[] {
    const extra = e.problema as unknown as { values?: unknown };
    if (Array.isArray(extra.values)) {
        return extra.values.map((v) => `«${String(v)}»`);
    }
    // Sin valores en la respuesta, los `errors` solo dicen qué fichas fallan (`values.<cliente>`): no se enseñan sus ids.
    return e.errores.map((x) => x.message ?? '').filter((x) => x !== '').map((x) => (x.startsWith('«') ? x : `«${x}»`));
}

// ---------------------------------------------------------------------------------------------------------------------

interface PropsEtiqueta {
    ops: Operaciones;
    negocio: string;
    movil: boolean;
    editable: boolean;
    abierto: { tipo: 'etiqueta'; etiqueta: EtiquetaCatalogo | null } | null;
    activas: readonly EtiquetaCatalogo[];
    versiones: ReadonlyMap<string, string>;
    onCerrar: () => void;
    onGuardada: (id: string, version: string | null) => void;
    onArchivar: (t: EtiquetaCatalogo) => void;
}

/** RV Panel de una etiqueta: nombre (único entre las activas), color y cómo se ve en la ficha. */
export function PanelEtiqueta({ ops, negocio, movil, editable, abierto, activas, versiones, onCerrar, onGuardada, onArchivar }: PropsEtiqueta) {
    const avisar = useAvisoBreve();
    const [visto, setVisto] = useState<PropsEtiqueta['abierto']>(null);
    const [nombre, setNombre] = useState('');
    const [color, setColor] = useState(COLORES[0]?.[0] ?? '#DD1420');
    const [intentado, setIntentado] = useState(false);
    const [errorServidor, setErrorServidor] = useState<string | null>(null);
    const [ocupado, setOcupado] = useState(false);
    if (abierto !== visto) {
        setVisto(abierto);
        setNombre(abierto?.etiqueta?.name ?? '');
        setColor(abierto?.etiqueta?.color ?? COLORES[0]?.[0] ?? '#DD1420');
        setIntentado(false);
        setErrorServidor(null);
    }
    const etiqueta = abierto?.etiqueta ?? null;
    const nueva = etiqueta === null;
    const limpio = nombre.trim();
    const repetida = activas.some((t) => t.id !== etiqueta?.id && t.name.toLocaleLowerCase('es') === limpio.toLocaleLowerCase('es'));
    const errorNombre = limpio === '' ? 'Escribe un nombre.' : repetida ? 'Ya hay una etiqueta activa con ese nombre.' : errorServidor;

    const guardar = () => {
        setIntentado(true);
        if (errorNombre !== null && errorNombre !== errorServidor) {
            return;
        }
        setOcupado(true);
        const datos = { name: limpio, color };
        (etiqueta === null
            ? ops.createTag(negocio, datos)
            : versionDe(versiones, etiqueta.id, () => ops.getTag(negocio, etiqueta.id)).then((v) => ops.updateTag(negocio, etiqueta.id, datos, v)))
            .then((r) => {
                avisar({ tipo: 'ok', texto: nueva ? `Etiqueta «${limpio}» creada.` : 'Cambios guardados.' });
                onGuardada(r.datos.id, r.version);
            })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.codigo === 'conflict') {
                    setErrorServidor('Ya hay una etiqueta activa con ese nombre.');
                    return;
                }
                avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido guardar la etiqueta. Prueba de nuevo.') });
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    return (
        <PanelLateral
            abierto={abierto !== null}
            onCerrar={onCerrar}
            movil={movil}
            titulo={nueva ? 'Nueva etiqueta' : limpio === '' ? 'Sin nombre' : limpio}
            eyebrow={nueva ? 'Etiqueta nueva' : 'Etiqueta'}
            subtitulo="Para agrupar clientes y filtrar la lista."
            icono="tag"
            accion={editable ? (nueva ? 'Crear etiqueta' : 'Guardar') : ''}
            ocupado={ocupado}
            onAccion={guardar}
        >
            <div className="rv-ca-cuerpo rv-cat-panel">
                <div className="rv-cat-panel__ayuda">
                    <Ayuda tarjetas={AYUDA.etiqueta} />
                </div>
                <Campo
                    etiqueta="Nombre de la etiqueta"
                    valor={nombre}
                    onChange={(v) => {
                        setNombre(v);
                        setErrorServidor(null);
                    }}
                    error={intentado ? errorNombre : null}
                    bloqueado={!editable}
                    max={60}
                    ayuda="Corto y claro: VIP, Habitual, Empresa…"
                    nombre="nombre-etiqueta"
                    onEnter={guardar}
                />
                <div className="rv-ca-bloque">
                    <strong className="rv-cat-subtitulo">Color</strong>
                    <div role="radiogroup" aria-label="Color" className="rv-cat-colores">
                        {COLORES.map(([hex, nombreColor]) => (
                            <button
                                key={hex}
                                type="button"
                                role="radio"
                                aria-checked={color === hex}
                                aria-label={nombreColor}
                                aria-disabled={!editable ? true : undefined}
                                className={`rv-cat-color${color === hex ? ' rv-cat-color--on' : ''}`}
                                style={{ background: hex, color: esClaro(hex) ? 'var(--rv-ink)' : '#fff' }}
                                onClick={() => {
                                    if (editable) {
                                        setColor(hex);
                                    }
                                }}
                            >
                                <Icono nombre="check" />
                            </button>
                        ))}
                    </div>
                </div>
                <div className="rv-cat-muestra">
                    <span>Así se ve en la ficha del cliente</span>
                    <span className="rv-cat-muestra__chip">
                        <span style={{ background: color }} aria-hidden="true" />
                        {limpio === '' ? 'Nombre de la etiqueta' : limpio}
                    </span>
                </div>
                {editable && etiqueta !== null && (
                    <div className="rv-cat-archivar">
                        <Boton tipo="secundario" rojo icono="archive" movil={movil} onClick={() => { onArchivar(etiqueta); }}>
                            Archivar etiqueta
                        </Boton>
                        <span>{TEXTOS.archivarEtiquetaNota}</span>
                    </div>
                )}
            </div>
        </PanelLateral>
    );
}

// ---------------------------------------------------------------------------------------------------------------------

interface PropsCampo {
    ops: Operaciones;
    negocio: string;
    movil: boolean;
    editable: boolean;
    abierto: { tipo: 'campo'; campo: CampoCatalogo | null } | null;
    activos: readonly CampoCatalogo[];
    todos: readonly CampoCatalogo[];
    versiones: ReadonlyMap<string, string>;
    onCerrar: () => void;
    onGuardado: (id: string, version: string | null) => void;
    onArchivar: (c: CampoCatalogo) => void;
}

interface Borrador {
    nombre: string;
    clave: string;
    claveTocada: boolean;
    tipo: TipoCampo;
    opciones: string[];
    obligatorio: boolean;
    posicion: string;
}

const conOpciones = (t: TipoCampo) => t === 'select' || t === 'multiselect';

/**
 * RV Panel de un campo propio: nombre, clave (no cambia después), tipo, opciones, obligatorio y posición. Si el campo ya
 * tiene valores (`has_values`, G152), avisa antes de cambiar el tipo; al guardar manda `type_change: convert` y, si la
 * API responde 409 con lo que no se puede convertir, lo enseña sin cambiar nada (D-073).
 */
export function PanelCampo({ ops, negocio, movil, editable, abierto, activos, todos, versiones, onCerrar, onGuardado, onArchivar }: PropsCampo) {
    const avisar = useAvisoBreve();
    const [visto, setVisto] = useState<PropsCampo['abierto']>(null);
    const [b, setB] = useState<Borrador>({ nombre: '', clave: '', claveTocada: false, tipo: 'text', opciones: ['', ''], obligatorio: false, posicion: '1' });
    const [intentado, setIntentado] = useState(false);
    const [conflicto, setConflicto] = useState<{ a: TipoCampo; valores: string[] } | null>(null);
    const [errorClave, setErrorClave] = useState<string | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const campo = abierto?.campo ?? null;
    if (abierto !== visto) {
        setVisto(abierto);
        setIntentado(false);
        setConflicto(null);
        setErrorClave(null);
        setB(
            campo === null
                ? { nombre: '', clave: '', claveTocada: false, tipo: 'text', opciones: ['', ''], obligatorio: false, posicion: String(activos.length + 1) }
                : { nombre: campo.label, clave: campo.key, claveTocada: true, tipo: campo.type, opciones: [...(campo.options ?? [])], obligatorio: campo.required, posicion: String(campo.position ?? activos.findIndex((c) => c.id === campo.id) + 1) },
        );
    }
    const nuevo = campo === null;
    const total = activos.length + (nuevo ? 1 : 0);
    const cambiaTipo = campo !== null && b.tipo !== campo.type;

    const errores = {
        nombre: b.nombre.trim() === '' ? 'Escribe un nombre.' : null,
        clave: !nuevo ? null : !/^[a-z][a-z0-9_]{1,39}$/.test(b.clave) ? 'Empieza por una letra; solo minúsculas, números y _.' : todos.some((c) => c.key === b.clave) ? 'Ya la usa otro campo.' : errorClave,
        opciones: conOpciones(b.tipo)
            ? b.opciones.map((o, i, todas) => (o.trim() === '' ? 'Escribe la opción o quítala.' : todas.findIndex((x) => x.trim().toLocaleLowerCase('es') === o.trim().toLocaleLowerCase('es')) < i ? 'Ya está en la lista.' : null))
            : [],
    };
    const hayErrores = errores.nombre !== null || errores.clave !== null || errores.opciones.some((x) => x !== null);

    const guardar = () => {
        setIntentado(true);
        if (hayErrores) {
            return;
        }
        setOcupado(true);
        const opciones = conOpciones(b.tipo) ? b.opciones.map((o) => o.trim()) : undefined;
        const posicion = Number(b.posicion);
        const peticion = (() => {
            if (campo === null) {
                return ops.createCustomField(negocio, { key: b.clave, label: b.nombre.trim(), type: b.tipo, required: b.obligatorio, position: posicion, ...(opciones === undefined ? {} : { options: opciones }) });
            }
            const cambios: CambiosCampo = { label: b.nombre.trim(), required: b.obligatorio, position: posicion };
            if (cambiaTipo) {
                cambios.type = b.tipo;
                if (campo.has_values === true) {
                    cambios.type_change = 'convert';
                }
            }
            if (opciones !== undefined) {
                cambios.options = opciones;
            }
            return versionDe(versiones, campo.id, () => ops.getCustomField(negocio, campo.id)).then((v) => ops.updateCustomField(negocio, campo.id, cambios, v));
        })();
        peticion
            .then((r) => {
                avisar({ tipo: 'ok', texto: nuevo ? `Campo «${b.nombre.trim()}» creado.` : 'Cambios guardados.' });
                onGuardado(r.datos.id, r.version);
            })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.codigo === 'conflict') {
                    if (cambiaTipo) {
                        setConflicto({ a: b.tipo, valores: noConvertibles(e) });
                    } else if (nuevo) {
                        setErrorClave('Ya la usa otro campo.');
                    } else {
                        avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido guardar el campo. Prueba de nuevo.') });
                    }
                    return;
                }
                avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido guardar el campo. Prueba de nuevo.') });
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    const cambiar = (cambio: Partial<Borrador>) => {
        setB((x) => ({ ...x, ...cambio }));
    };

    return (
        <PanelLateral
            abierto={abierto !== null}
            onCerrar={onCerrar}
            movil={movil}
            titulo={nuevo ? 'Nuevo campo' : b.nombre.trim() === '' ? 'Sin nombre' : b.nombre.trim()}
            eyebrow={nuevo ? 'Campo nuevo' : 'Campo propio'}
            subtitulo={nuevo ? 'Se pide en la ficha de cada cliente.' : `Clave ${b.clave}`}
            icono={TIPO[b.tipo].icono}
            accion={editable ? (nuevo ? 'Crear campo' : 'Guardar') : ''}
            ocupado={ocupado}
            onAccion={guardar}
        >
            <div className="rv-ca-cuerpo rv-cat-panel">
                <div className="rv-cat-panel__ayuda">
                    <Ayuda tarjetas={AYUDA.campo} />
                </div>
                <div className="rv-ca-bloque">
                    <Campo
                        etiqueta="Nombre del campo"
                        valor={b.nombre}
                        onChange={(v) => {
                            cambiar({ nombre: v, ...(nuevo && !b.claveTocada ? { clave: claveDe(v) } : {}) });
                        }}
                        error={intentado ? errores.nombre : null}
                        bloqueado={!editable}
                        max={80}
                        ayuda="Así aparece en la ficha del cliente."
                        nombre="nombre-campo"
                    />
                    <Campo
                        etiqueta="Clave para integraciones"
                        valor={b.clave}
                        onChange={(v) => {
                            setErrorClave(null);
                            cambiar({ clave: v.toLocaleLowerCase('es').replace(/[^a-z0-9_]/g, ''), claveTocada: true });
                        }}
                        error={intentado ? errores.clave : null}
                        bloqueado={!editable || !nuevo}
                        max={40}
                        mayusculas="none"
                        ayuda={nuevo ? 'Para integraciones y el CSV. No se cambia después.' : 'No se puede cambiar.'}
                        nombre="clave-campo"
                    />
                </div>
                <div className="rv-ca-bloque">
                    <strong className="rv-cat-subtitulo">Tipo</strong>
                    <div role="radiogroup" aria-label="Tipo de campo" className="rv-cat-tipos">
                        {TIPOS.map((t) => (
                            <OpcionRadio
                                key={t.tipo}
                                elegida={b.tipo === t.tipo}
                                icono={t.icono}
                                titulo={t.nombre}
                                texto={t.ejemplo}
                                bloqueada={!editable}
                                onElegir={() => {
                                    setConflicto(null);
                                    cambiar({ tipo: t.tipo, opciones: conOpciones(t.tipo) && b.opciones.length === 0 ? ['', ''] : b.opciones });
                                }}
                            />
                        ))}
                    </div>
                </div>
                {cambiaTipo && conflicto === null && campo.has_values === true && (
                    <div role="status" className="rv-ca-caja rv-ca-caja--aviso">
                        <span className="rv-ca-caja__fila">
                            <Icono nombre="triangle-alert" />
                            <span>{`Este campo ya tiene valores en algunos clientes. Al guardar, pasan de ${TIPO[campo.type].nombre.toLocaleLowerCase('es')} a ${TIPO[b.tipo].nombre.toLocaleLowerCase('es')}. Si alguno no se puede convertir, no se cambia nada y te enseño cuáles.`}</span>
                        </span>
                    </div>
                )}
                {conflicto !== null && campo !== null && (
                    <div role="alert" className="rv-ca-caja rv-ca-caja--error rv-cat-conflicto">
                        <span className="rv-ca-caja__fila rv-cat-conflicto__titulo">
                            <Icono nombre="circle-alert" />
                            <span>{`Algunos valores no se pueden convertir a ${TIPO[conflicto.a].nombre.toLocaleLowerCase('es')}`}</span>
                        </span>
                        {conflicto.valores.length > 0 && (
                            <span className="rv-cat-conflicto__valores">
                                {conflicto.valores.map((v) => (
                                    <span key={v}>{v}</span>
                                ))}
                            </span>
                        )}
                        <span className="rv-cat-conflicto__nota">{TEXTOS.conflictoNota}</span>
                        <span className="rv-cat-conflicto__botones">
                            <Boton
                                tipo="secundario"
                                icono="undo-2"
                                onClick={() => {
                                    setConflicto(null);
                                    cambiar({ tipo: campo.type });
                                }}
                            >
                                {`Volver a ${TIPO[campo.type].nombre.toLocaleLowerCase('es')}`}
                            </Boton>
                            <Boton tipo="secundario" rojo icono="archive" onClick={() => { onArchivar(campo); }}>
                                Archivar este campo
                            </Boton>
                        </span>
                    </div>
                )}
                {conOpciones(b.tipo) && (
                    <div className="rv-ca-bloque">
                        <strong className="rv-cat-subtitulo">Opciones</strong>
                        {b.opciones.map((o, i) => (
                            <div key={i} className="rv-cat-opcion">
                                <Campo
                                    etiqueta={`Opción ${String(i + 1)}`}
                                    valor={o}
                                    onChange={(v) => {
                                        cambiar({ opciones: b.opciones.map((x, j) => (j === i ? v : x)) });
                                    }}
                                    error={intentado ? (errores.opciones[i] ?? null) : null}
                                    bloqueado={!editable}
                                    max={80}
                                />
                                {editable && b.opciones.length > 1 && (
                                    <Boton tipo="secundario" rojo icono="x" aria-label={`Quitar la opción ${String(i + 1)}`} className="rv-cat-quitar" onClick={() => { cambiar({ opciones: b.opciones.filter((_, j) => j !== i) }); }}>
                                        Quitar
                                    </Boton>
                                )}
                            </div>
                        ))}
                        {editable && (
                            <Boton tipo="secundario" icono="plus" className="rv-cat-anadir" onClick={() => { cambiar({ opciones: [...b.opciones, ''] }); }}>
                                Añadir opción
                            </Boton>
                        )}
                        {editable && !nuevo && <span className="rv-ca-nota rv-ca-nota--gris">{TEXTOS.opcionesNota}</span>}
                    </div>
                )}
                <div className="rv-cat-obligatorio">
                    <Interruptor etiqueta="Obligatorio" ayuda={TEXTOS.obligatorioAyuda} valor={b.obligatorio} bloqueado={!editable} linea="ninguna" onChange={(v) => { cambiar({ obligatorio: v }); }} />
                </div>
                <Desplegable
                    etiqueta="Posición en la ficha"
                    valor={b.posicion}
                    opciones={Array.from({ length: Math.max(1, total) }, (_, i) => ({ valor: String(i + 1), texto: i === 0 ? '1 · la primera' : String(i + 1) }))}
                    onChange={(v) => { cambiar({ posicion: v }); }}
                    bloqueado={!editable}
                />
                {editable && campo !== null && conflicto === null && (
                    <div className="rv-cat-archivar">
                        <Boton tipo="secundario" rojo icono="archive" movil={movil} onClick={() => { onArchivar(campo); }}>
                            Archivar campo
                        </Boton>
                        <span>{campo.has_values === true ? TEXTOS.archivarCampoConValores : TEXTOS.archivarCampoSinValores}</span>
                    </div>
                )}
            </div>
        </PanelLateral>
    );
}
